import React, {type ReactNode} from 'react';

import {Slot} from '../Slot';
import {OverlayPortal} from '../OverlayPortal';
import {FloatingNode, type Placement, type Side} from '@floating-ui/react';
import {useThemeContext} from '@packages/utilities/styled';
import {
  useDevAlert,
  useIsomorphicLayoutEffect,
} from '@packages/systems/spring-hooks';

import {hasTabbableAncestor, hasTabbableChildren} from '../../utils';
import {MenuContentContext} from '../Menu/useMenuContent';

import {
  TooltipWrapperView,
  TooltipContentView,
  TooltipArrow,
  TooltipArrowShadow,
  TooltipTitle,
} from './Tooltip.styles';
import {useTooltip, TooltipContext, useTooltipTransition} from './useTooltip';

import {useAnchorRefPosition} from '../../hooks';
import type {TooltipProps, TooltipColorVariant} from './_types';

/**
 * Used to coordinate nested `Tooltip`s.
 * We do not want to render multiple, overlapping `Tooltip`s on accident.
 */
const NestedTooltipContext = React.createContext({
  isAncestorEnabled: false,
});

/**
 * # Tooltip
 *
 * Tooltip displays an informative message that appears when a user interacts with an element. Tooltips are usually initiated in one of two ways: through a mouse-hover gesture or through a keyboard-hover gesture.
 *
 * ## Usage
 *
 * ```jsx
 * import {Tooltip} from '@packages/systems/spring';
 *
 * const Example = () => {
 *   return <Tooltip label="Tooltip content">Hover me</Tooltip>;
 * }
 * ```
 *
 * @component
 */
export const Tooltip = ({
  asChild,
  'aria-label': ariaLabel,
  anchorRef,
  colorVariant,
  label: labelProp,
  defaultIsOpen,
  delay,
  density,
  hasArrow,
  horizontalOffset,
  isDisabled: isDisabledProp,
  isHoverable: isHoverableProp,
  isOpen,
  maxWidth = 280,
  minWidth = 'min-content',
  width,
  onOpenChange,
  placement = 'top',
  children,
  'data-automation-id': dataAutomationId,
  content,
  shouldCloseOnClick = true,
  shouldEscapeViewport = true,
  _shouldNotBeTabbable,
  title,
  variant = 'default',
  verticalOffset,
  _useDismissProps,
  ...props
}: TooltipProps) => {
  useDevAlert(
    (Boolean(content) || Boolean(labelProp)) !== true && Boolean(title),
    'You have provided a title for your Tooltip, but no label or content. Please provide a label.'
  );

  const {isAncestorEnabled} = React.useContext(NestedTooltipContext);

  /**
   * If we have an enabled ancestor, we want to disable this Tooltip.
   */
  const isDisabled = isAncestorEnabled || isDisabledProp;

  const {
    colorVariant: tooltipGroupContextColorVariant,
    density: tooltipGroupContextDensity,
    hasArrow: tooltipGroupContextHasArrow,
    placement: tooltipGroupContextPlacement,
    horizontalOffset: tooltipGroupContextHorizontalOffset,
    isHoverable: tooltipGroupContextIsHoverable,
    verticalOffset: tooltipGroupContextVerticalOffset,
    shouldEscapeViewport: tooltipGroupContextShouldEscapeViewport,
  } = React.useContext(TooltipGroupContext);
  const Component = asChild ? Slot : 'span';
  /**
   * Create ref here so that it can be passed to useTooltip for arrow positioning.
   * It will not attach to the FloatingArrow component if we make the ref inside useTooltip,
   */
  const arrowRef = React.useRef(null);

  const tooltip = useTooltip({
    placement: tooltipGroupContextPlacement ?? placement,
    defaultIsOpen,
    delay,
    horizontalOffset: tooltipGroupContextHorizontalOffset ?? horizontalOffset,
    isDisabled,
    isOpen,
    onOpenChange,
    arrowRef,
    maxWidth,
    minWidth,
    isHoverable: isHoverableProp,
    shouldCloseOnClick,
    shouldEscapeViewport:
      tooltipGroupContextShouldEscapeViewport ?? shouldEscapeViewport,
    _useDismissProps,
    verticalOffset: tooltipGroupContextVerticalOffset ?? verticalOffset,
  });

  const {refs, getReferenceProps, context, nodeId} = tooltip;
  const {__internalTheme} = useThemeContext();
  const {isWithinMenuContent: isWithinMenu} =
    React.useContext(MenuContentContext);

  const definitelyNotTabbable =
    isAncestorEnabled || isDisabled || isWithinMenu || _shouldNotBeTabbable;
  const [hasTabbableFamily, setHasTabbableFamily] = React.useState(false);

  /**
   * We need to "continue" the box-shadow of our TooltipContentWrapper.
   * It uses inset box shadows. SVGs do not have box-shadow, only drop-shadow. Drop shadows do not have an inset property.
   * In addition, the shadows are dark in both light & dark theme.
   *
   * This means that we need to render out special shadows for our arrows, and that they need to be different between the themes integrate in their respective backgrounds properly.
   *
   * For dark theme, we are using a second arrow, which renders its own drop-shadow.
   * For light theme, we need do not have this shadow arrow. Instead, we use a filter to drop-shadow the entire tooltip, giving a subtle background for the arrow to show up.
   * */
  const isDarkTheme = __internalTheme?.toLowerCase?.()?.includes('dark');

  useIsomorphicLayoutEffect(() => {
    /**
     * We want to ensure that our Tooltips are tab-focusable.
     * However, we only want to add our own tab-index if none of their children or ancestors are tabbable.
     *
     * We are running this in a useEffect since we need to run after the refs are set.
     */
    if (refs.domReference?.current) {
      const domRef = refs.domReference.current as HTMLElement;
      if (hasTabbableAncestor(domRef) || hasTabbableChildren(domRef)) {
        setHasTabbableFamily(true);
        return;
      }
    }

    setHasTabbableFamily(false);
  }, [refs, definitelyNotTabbable]);

  useAnchorRefPosition({anchorRef, context});

  const currentPlacement: Placement = context?.placement ?? 'bottom';
  const currentPlacementSide = currentPlacement.replace(
    /-(start|end)/,
    ''
  ) as Side;

  /**
   * Floating-UI's `useListNavigation` uses a tabIndex of -1 as an indicator of how to handle focus.
   *
   * @see: https://floating-ui.com/docs/uselistnavigation#using-with-floatingfocusmanager
   */

  const tabIndexProp =
    hasTabbableFamily || definitelyNotTabbable ? undefined : {tabIndex: 0};
  const _colorVariant = tooltipGroupContextColorVariant ?? colorVariant;
  const _density = tooltipGroupContextDensity ?? density ?? 'comfortable';
  const _hasArrow =
    tooltipGroupContextHasArrow ??
    hasArrow ??
    (_density === 'comfortable' && variant !== 'commentBubble');

  const nestedContextValue = React.useMemo(
    () => ({
      isNested: true,
      isAncestorEnabled: isAncestorEnabled || !isDisabled,
    }),
    [isAncestorEnabled, isDisabled]
  );

  return (
    <NestedTooltipContext.Provider value={nestedContextValue}>
      <TooltipContext.Provider value={tooltip}>
        <Component
          ref={refs.setReference}
          {...getReferenceProps(props)}
          {...tabIndexProp}
        >
          {children}
        </Component>
        <FloatingNode id={nodeId}>
          <TooltipContent
            ariaLabel={ariaLabel}
            colorVariant={_colorVariant}
            currentPlacement={currentPlacement}
            currentPlacementSide={currentPlacementSide}
            data-automation-id={dataAutomationId}
            density={_density}
            isHoverable={isHoverableProp ?? tooltipGroupContextIsHoverable}
            isLightMode={_hasArrow && !isDarkTheme}
            variant={variant}
            width={width}
          >
            {/* Tooltip Arrow goes here, not Tooltip content, so closing animations work correctly in TooltipGroups */}
            {_hasArrow ? (
              <TooltipArrow
                colorVariant={_colorVariant}
                context={context}
                data-arrow="true"
                ref={arrowRef}
              />
            ) : null}
            {/* To achieve the dropshadow effect, we place a second arrow (w/ shadows) and bump it just a tad  */}
            {_hasArrow && isDarkTheme ? (
              <>
                <TooltipArrowShadow
                  colorVariant={_colorVariant}
                  context={context}
                  data-arrow="true"
                  placement={currentPlacementSide}
                  ref={arrowRef}
                  style={{
                    transform: 'translateY(-1px)', // bump it down to lay the shadow over the base arrow
                  }}
                />
              </>
            ) : null}
            {/* This second arrow is used to mask the content wrapper's shadows, as well as the bottom half of the ArrowShadow's drop-shadow if we're in dark mode */}
            {_hasArrow ? (
              <TooltipArrow
                colorVariant={_colorVariant}
                context={context}
                data-arrow="true"
                ref={arrowRef}
                style={{
                  transform: 'translateY(-2px)', // bump this down to cover the shadow arrow's bottom shadow transition.
                }}
              />
            ) : null}

            {title ? <TooltipTitle>{title}</TooltipTitle> : null}
            {labelProp ?? content}
          </TooltipContent>
        </FloatingNode>
      </TooltipContext.Provider>
    </NestedTooltipContext.Provider>
  );
};

Tooltip.displayName = 'Tooltip';

type TooltipContentProps = {
  ariaLabel?: string;
  colorVariant?: TooltipColorVariant;
  currentPlacement?: Placement;
  currentPlacementSide?: Side;
  density?: 'comfortable' | 'compact';
  isLightMode?: boolean;
  isHoverable?: boolean;
  variant: 'default' | 'commentBubble';
  width?: React.CSSProperties['width'];
  children: ReactNode;
};

const TooltipContent = React.memo(
  ({
    ariaLabel,
    colorVariant,
    currentPlacement,
    currentPlacementSide,
    density,
    isHoverable,
    isLightMode,
    variant,
    width,
    children,
    ...rest
  }: TooltipContentProps) => {
    const {
      isMounted,
      transitionStyles,
      x,
      y,
      setFloating,
      strategy,
      getFloatingProps,
    } = useTooltipTransition();

    if (!isMounted) return null;

    return (
      <OverlayPortal>
        <TooltipWrapperView
          {...rest}
          aria-label={ariaLabel}
          colorVariant={colorVariant}
          density={density}
          lightModeShadowPlacement={
            isLightMode ? currentPlacementSide : undefined
          }
          placement={currentPlacement}
          ref={setFloating}
          style={{
            top: y,
            left: x,
            width: width ?? 'max-content',
            position: strategy,
            pointerEvents: isHoverable ? 'auto' : 'none',
            ...transitionStyles,
          }}
          variant={variant}
          {...getFloatingProps()}
        >
          <TooltipContentView>{children}</TooltipContentView>
        </TooltipWrapperView>
      </OverlayPortal>
    );
  }
);

export default Tooltip;

/** Defining TooltipGroup context here to avoid circular dependency */
type TooltipGroupContextType = Pick<
  TooltipProps,
  | 'colorVariant'
  | 'density'
  | 'placement'
  | 'hasArrow'
  | 'horizontalOffset'
  | 'isHoverable'
  | 'verticalOffset'
  | 'shouldEscapeViewport'
>;
export const TooltipGroupContext = React.createContext<TooltipGroupContextType>(
  {}
);
